import Vue from "vue";

const BLOCKER_KEYS = {
    amount_over_limit: "purchaseApproval.blockers.amountOverLimit",
    approval_pending: "purchaseApproval.blockers.approvalPending",
    approval_not_pending: "purchaseApproval.blockers.approvalNotPending",
    approval_required: "purchaseApproval.blockers.approvalRequired",
    validate_permission_required:
        "purchaseApproval.blockers.validatePermissionRequired",
    change_permission_required:
        "purchaseApproval.blockers.changePermissionRequired",
    incomplete_order: "purchaseApproval.blockers.incompleteOrder",
    missing_delivery_address: "purchaseApproval.blockers.missingDeliveryAddress",
    incomplete_entity_info: "purchaseApproval.blockers.incompleteEntityInfo",
    order_seller_address_missing: "purchaseApproval.blockers.orderSellerAddressMissing",
    ORDER_ALREADY_READY: "purchaseApproval.blockers.orderAlreadyReady",
    approval_suborder_readonly:
        "purchaseApproval.blockers.approvalSuborderReadonly",
};

export function translatePurchaseApprovalBlocker(vm, code) {
    const key = BLOCKER_KEYS[code];
    if (key && vm.$translate) {
        const translated = vm.$translate(key);
        if (translated !== key) {
            return translated;
        }
    }
    return code;
}

export function formatPurchaseApprovalBlockers(vm, blockers) {
    if (!blockers || !blockers.length) {
        return [];
    }
    return blockers.map((code) => translatePurchaseApprovalBlocker(vm, code));
}

export function formatPurchaseApprovalAmountBasis(vm, basis) {
    if (basis === "incl_vat") {
        return vm.$translate("purchaseApproval.amountBasisInclVat");
    }
    return vm.$translate("purchaseApproval.amountBasisExclVat");
}

export function formatPurchaseApprovalAmount(vm, preview) {
    if (!preview || preview.order_amount == null) {
        return null;
    }
    const amount = vm.$utils.price(preview.order_amount);
    const basis = formatPurchaseApprovalAmountBasis(vm, preview.amount_basis);
    return `${amount} (${basis})`;
}

export function formatPurchaseApprovalLimit(vm, preview) {
    if (!preview || preview.effective_limit == null) {
        return vm.$translate("purchaseApproval.unlimited");
    }
    return vm.$utils.price(preview.effective_limit);
}

export function extractPurchaseApprovalError(error) {
    const details = error?.details || error || {};
    const blockers = Array.isArray(details.blockers) ? details.blockers : [];
    const businessCode = typeof details.code === "string" ? details.code : null;
    const nonFieldErrors = Array.isArray(details.non_field_errors)
        ? details.non_field_errors.filter(Boolean)
        : [];
    return {
        businessCode,
        blockers,
        nonFieldErrors,
        status: error?.code,
        fulfillmentIncompleteScopes: details.fulfillment_incomplete_scopes || [],
    };
}

export function isPurchaseApprovalBlockerError(error) {
    const parsed = extractPurchaseApprovalError(error);
    if (parsed.status !== 412) {
        return false;
    }
    if (parsed.fulfillmentIncompleteScopes.length > 0) {
        return false;
    }
    return (
        parsed.blockers.length > 0
        || parsed.nonFieldErrors.length > 0
        || !!parsed.businessCode
    );
}

export function resolvePurchaseApprovalBlockerMessages(vm, error) {
    const parsed = extractPurchaseApprovalError(error);
    let rawBlockers = [...parsed.blockers];
    if (!rawBlockers.length && parsed.nonFieldErrors.length) {
        rawBlockers = parsed.nonFieldErrors;
    } else if (!rawBlockers.length && parsed.businessCode) {
        rawBlockers = [parsed.businessCode];
    }
    const blockers = formatPurchaseApprovalBlockers(vm, rawBlockers);
    if (!blockers.length) {
        blockers.push(vm.$translate("purchaseApproval.blockers.unknown"));
    }
    return blockers;
}

export async function showPurchaseApprovalBlockersModal(vm, { error, orderId }) {
    const blockers = resolvePurchaseApprovalBlockerMessages(vm, error);
    const PurchaseApprovalBlockersModal = (
        await import("@/modals/purchase-approval-blockers.vue")
    ).default;
    const result = await vm.showModal({
        component: PurchaseApprovalBlockersModal,
        params: {
            blockers,
            orderId,
        },
    });
    return result;
}

export function getProviderOrderBuyerEntityId(providerorder) {
    if (!providerorder) {
        return null;
    }
    if (providerorder.buyer_entity != null) {
        return providerorder.buyer_entity;
    }
    const buyerEntityFromData =
        providerorder.buyer_data?.entity ||
        providerorder.buyer_data?.entity_id ||
        null;
    if (buyerEntityFromData != null) {
        return buyerEntityFromData;
    }
    return providerorder.buyer_data?.owner ?? null;
}

export function shouldFetchPurchaseApprovalPreview(vm) {
    if (!vm || vm.isReadOnly || vm.isOperator) {
        return false;
    }
    if (!vm.userHasPermission || !vm.userHasPermission("order.view_order")) {
        return false;
    }
    const providerorder = vm.providerorder;
    if (!providerorder || providerorder.readonly) {
        return false;
    }
    // Purchase approval preview is buyer-only (API: IsOrderBuyer).
    return true;
}

export function userCanValidateProviderOrder(vm) {
    return (
        vm?.userHasPermission &&
        vm.userHasPermission("order.validate_providerorder")
    );
}

export function userCanChangeProviderOrder(vm) {
    return (
        vm?.$utils?.userHasProviderOrderPermission &&
        vm.$utils.userHasProviderOrderPermission("change")
    );
}

/** Buyer may email the PO to the supplier (API: IsOrderHandler on sendformtoprovider). */
export function canSendProviderOrderFormToProvider(vm) {
    if (!vm || vm.current_entity_id == null) {
        return false;
    }
    const providerorder = vm.providerorder;
    if (!providerorder || providerorder.readonly || !providerorder.ready) {
        return false;
    }
    if (providerorder.status === "canceled") {
        return false;
    }
    return true;
}

export function providerOrderDeliveredToStock(providerorder) {
    return !!providerorder?.deliverysite_data?.stock;
}

export function providerOrderNeedsProductStrategy(providerorder) {
    return (
        !providerorder?.recipient &&
        providerOrderDeliveredToStock(providerorder)
    );
}

export function isApproverValidatingPendingProviderOrder(vm) {
    const providerorder = vm?.providerorder;
    if (!providerorder || providerorder.purchase_approval_status !== "pending") {
        return false;
    }
    const submittedById =
        providerorder.purchase_approval_submitted_by_data?.id ||
        providerorder.purchase_approval_submitted_by;
    return (
        submittedById != null && submittedById !== vm.current_user_id
    );
}

export function shouldAutoSendProviderOrderFormAfterValidation(
    vm,
    result,
    { wasApprovalValidation = false } = {}
) {
    return (
        result?.ready &&
        result?.purchase_approval_status !== "pending" &&
        !result?.seller_entity &&
        !wasApprovalValidation
    );
}

export async function resolveProviderOrderProductStrategy(vm) {
    if (!providerOrderNeedsProductStrategy(vm.providerorder)) {
        return null;
    }
    const productstrategy = await vm.$store.dispatch(
        "providerorder/fetchProductStrategy"
    );
    if (!vm.$store.getters["providerorder/productStrategyNeedsUserInput"]) {
        return productstrategy;
    }
    const ProductStrategy = (await import("@/modals/productstrategy")).default;
    const result = await vm.showModal({
        component: ProductStrategy,
        params: {
            providerorder: vm.providerorder,
            productstrategy,
        },
    });
    if (!result) {
        return undefined;
    }
    return result;
}

/**
 * Seller-submitted customer order awaiting connected buyer confirmation
 * (``ready`` set by seller, ``buyer_validated`` still false).
 */
export function isCustomerOrderAwaitingBuyerConfirmation(customerorder) {
    return (
        !!customerorder?.buyer_entity &&
        !!customerorder?.submitted_for_buyer_validation &&
        !customerorder?.buyer_validated
    );
}

/**
 * Seller-submitted connected order awaiting buyer confirmation
 * (``ready`` set by seller, ``validated`` / ``buyer_validated`` still false).
 */
export function isProviderOrderAwaitingBuyerConfirmation(providerorder) {
    return (
        !!providerorder?.submitted_for_buyer_validation &&
        !providerorder?.validated
    );
}

/**
 * Provider order confirmed by the buyer (``validated`` / ``buyer_validated``)
 * or logistics-ready without a pending buyer gate (offline PO, seller waiver, legacy).
 */
export function isProviderOrderValidated(providerorder) {
    if (!providerorder) {
        return false;
    }
    if (isProviderOrderAwaitingBuyerConfirmation(providerorder)) {
        return false;
    }
    if (providerorder.validated) {
        return true;
    }
    if (providerorder.ready) {
        return true;
    }
    if (providerorder.validated_date) {
        return true;
    }
    // Defensive fallback when ``validated_date`` is missing (legacy rows).
    if (
        providerorder.status === "processing" ||
        providerorder.status === "completed"
    ) {
        return true;
    }
    return (
        providerorder.status === "to_be_processed" &&
        !providerOrderDeliveredToStock(providerorder) &&
        !providerorder.seller_entity
    );
}

/** Buyer may finalize an offsite provider order (API: CanCompleteProviderOrder). */
export function canCompleteOffsiteProviderOrder(vm) {
    if (!vm || vm.current_entity_id == null) {
        return false;
    }
    const providerorder = vm.providerorder;
    if (
        !providerorder ||
        providerorder.readonly ||
        !isProviderOrderValidated(providerorder) ||
        providerOrderDeliveredToStock(providerorder) ||
        providerorder.status === "completed" ||
        providerorder.status === "canceled" ||
        providerorder.seller_entity
    ) {
        return false;
    }
    return true;
}

/** Offline provider (no seller entity on the order or on seller_data). */
export function providerSellerIsNotConnected(providerorder) {
    if (!providerorder) {
        return false;
    }
    return !providerorder.seller_entity && !providerorder.seller_data?.entity;
}

const ACTIVE_NON_TERMINAL_PROVIDER_ORDER_STATUSES = new Set([
    "waiting",
    "processing",
    "to_be_processed",
]);

export function connectedSellerHasValidated(providerorder) {
    return !!providerorder?.connected_seller_validated;
}

export function providerOrderHasHandlingReceipt(receipts) {
    if (!Array.isArray(receipts)) {
        return false;
    }
    return receipts.some(
        (receipt) => receipt.status === "handling" || receipt.status === "handled"
    );
}

export function providerOrderLinesLocked(providerorder, receipts) {
    if (!providerorder) {
        return true;
    }
    if (providerorder.purchase_approval_status === "pending") {
        return true;
    }
    if (connectedSellerHasValidated(providerorder)) {
        return true;
    }
    if (providerorder.seller_entity && providerorder.status === "processing") {
        return true;
    }
    if (providerOrderHasHandlingReceipt(receipts)) {
        return true;
    }
    return false;
}

/** Buyer may invalidate (ready=false) an active provider order. */
export function canInvalidateProviderOrder(vm) {
    if (!vm?.userHasPermission?.("order.validate_providerorder")) {
        return false;
    }
    const providerorder = vm.providerorder;
    if (!providerorder || providerorder.parent || !providerorder.ready) {
        return false;
    }
    if (providerorder.readonly || providerorder.scheduled) {
        return false;
    }
    if (connectedSellerHasValidated(providerorder)) {
        return false;
    }
    if (providerorder.status === "processing") {
        return false;
    }
    return ACTIVE_NON_TERMINAL_PROVIDER_ORDER_STATUSES.has(providerorder.status);
}

/** Buyer may reopen a finalized provider order (POST /reopen/). */
export function canReopenCompletedProviderOrder(vm) {
    if (!vm?.userHasPermission?.("order.validate_providerorder")) {
        return false;
    }
    const providerorder = vm.providerorder;
    if (!providerorder || providerorder.parent || !providerorder.readonly) {
        return false;
    }
    if (connectedSellerHasValidated(providerorder)) {
        return false;
    }
    return (
        providerorder.status === "completed" ||
        providerorder.status === "canceled"
    );
}

/** Buyer may invalidate (ready=false) an active offline provider order. */
export function canInvalidateNonConnectedProviderOrder(vm) {
    return canInvalidateProviderOrder(vm);
}

/** Buyer may reopen a finalized offline provider order (POST /reopen/). */
export function canReopenCompletedNonConnectedProviderOrder(vm) {
    return canReopenCompletedProviderOrder(vm);
}

export function canShowReopenNonConnectedProviderOrder(vm) {
    return (
        canInvalidateProviderOrder(vm) ||
        canReopenCompletedProviderOrder(vm)
    );
}
